/**
 * Phase 21.4 Batch 3D — My Contest Submissions (/contests/my).
 *
 * Auth-required. Reads all entries via /contests/me/entries.
 * Groups nothing — a flat, chronological list keeps mobile UX simple.
 */
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import SharedNavigation from "@/components/SharedNavigation";
import ContestStatusBadge from "@/components/contests/ContestStatusBadge";
import { entryDisplayStatus } from "@/lib/contestStatus";
import api from "@/lib/api";
import { getCoverUrl } from "@/lib/mediaUrls";
import { toast } from "sonner";

const MyContestSubmissionsPage = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(null);

  const isAuthed = typeof window !== "undefined" && !!localStorage.getItem("session_token");

  useEffect(() => {
    if (!isAuthed) {
      navigate(`/login?next=${encodeURIComponent("/contests/my")}`);
    }
  }, [isAuthed, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/contests/me/entries");
      if (r.data?.enabled === false) {
        setEnabled(false);
        setRows([]);
      } else {
        setEnabled(true);
        setRows(r.data?.entries || []);
      }
    } catch (e) {
      toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAuthed) load(); }, [isAuthed, load]);

  if (!isAuthed) return null;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-contest-submissions-page">
      <SharedNavigation />
      <div className="max-w-4xl mx-auto pt-24 px-4 pb-16 space-y-6">
        <div>
          <Link to="/contests" className="text-xs text-purple-300 hover:underline" data-testid="my-submissions-back">
            ← Contest Center
          </Link>
          <h1 className="text-3xl sm:text-4xl font-black mt-1" data-testid="my-submissions-title">
            My Contest Submissions
          </h1>
          <p className="text-sm text-gray-400 mt-1">Every entry you&apos;ve made across every contest.</p>
        </div>

        {enabled === false && (
          <div
            className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 text-center"
            data-testid="my-submissions-flag-off"
          >
            <p className="text-3xl mb-2">🏆</p>
            <p className="text-white font-black">Contests are coming soon</p>
            <p className="text-xs text-gray-400 mt-1">You&apos;ll see your entries here once contests launch.</p>
          </div>
        )}

        {loading && <p className="text-sm text-gray-500" data-testid="my-submissions-loading">Loading…</p>}

        {enabled && !loading && rows.length === 0 && (
          <div
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-8 text-center"
            data-testid="my-submissions-empty"
          >
            <p className="text-4xl mb-3">🎤</p>
            <p className="text-white font-black">No entries yet</p>
            <p className="text-xs text-gray-400 mt-1 mb-4">Browse open contests and enter to win reward coins and promo plays.</p>
            <Link
              to="/contests"
              className="inline-flex items-center px-4 py-2 rounded-full bg-white text-black text-xs font-black hover:bg-amber-200"
              data-testid="my-submissions-browse-btn"
            >
              Browse contests →
            </Link>
          </div>
        )}

        {enabled && !loading && rows.length > 0 && (
          <div className="space-y-3" data-testid="my-submissions-list">
            {rows.map((e) => {
              const es = entryDisplayStatus(e);
              const c = e.contest || {};
              // Canonical cover resolution — route through getCoverUrl
              // so legacy / bare-filename / null snapshots render the
              // branded cover-unavailable placeholder instead of the
              // mic emoji. Never substitute an artist avatar.
              const _snap = e.ref_snapshot || {};
              const _rawCover = _snap.cover_url || _snap.cover_art;
              const cover = _rawCover
                ? getCoverUrl({ cover_url: _rawCover, cover_art: _snap.cover_art })
                : getCoverUrl(null);
              const title = _snap.title || e.ref_id;
              const contestHref = c.slug ? `/contests/${c.slug}` : "/contests";
              const isWinner = e.status === "winner" && e.position;
              const winnerHref = isWinner && c.slug ? `/contests/${c.slug}/results/${e.entry_id}` : null;
              // Phase 21.4 Batch 10 — Clickable entry: taps go to the
              // exact track/beat page, not the artist profile.
              const entryHref = e.entry_type === "beat"
                ? (e.ref_id ? `/beats/${e.ref_id}` : null)
                : (e.ref_id ? `/track/${e.ref_id}` : null);
              return (
                <div
                  key={e.entry_id}
                  className="rounded-xl border border-white/10 bg-white/[0.03] p-3 sm:p-4 flex flex-wrap sm:flex-nowrap items-center gap-3"
                  data-testid={`my-submission-row-${e.entry_id}`}
                >
                  {entryHref ? (
                    <Link
                      to={entryHref}
                      className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 relative"
                      data-testid={`my-submission-cover-link-${e.entry_id}`}
                    >
                      <img
                        src={cover}
                        alt={title}
                        className="w-full h-full object-cover"
                        data-testid={`my-submission-cover-${e.entry_id}`}
                      />
                    </Link>
                  ) : (
                    <div className="w-14 h-14 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0 relative">
                      <img
                        src={cover}
                        alt={title}
                        className="w-full h-full object-cover"
                        data-testid={`my-submission-cover-${e.entry_id}`}
                      />
                    </div>
                  )}
                  <div className="min-w-0 flex-1 order-3 sm:order-2 basis-full sm:basis-auto">
                    <Link
                      to={contestHref}
                      className="text-white font-black text-sm hover:text-purple-200 truncate block"
                      data-testid={`my-submission-contest-link-${e.entry_id}`}
                    >
                      {c.title || "Contest"}
                    </Link>
                    <p className="text-xs text-gray-400 truncate">
                      <span className="text-gray-300">Entry:</span>{" "}
                      {entryHref ? (
                        <Link
                          to={entryHref}
                          className="hover:text-purple-200 underline decoration-purple-500/40"
                          data-testid={`my-submission-entry-link-${e.entry_id}`}
                        >
                          {title}
                        </Link>
                      ) : title}
                    </p>
                    <p className="text-[10px] text-gray-500">
                      Submitted {e.created_at ? new Date(e.created_at).toLocaleDateString() : "—"}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 order-2 sm:order-3 ml-auto">
                    <span
                      className={`inline-flex items-center rounded-full font-black text-[10px] px-2 py-0.5 border ${es.tone}`}
                      data-testid={`my-submission-status-${e.entry_id}`}
                    >
                      {es.label}
                    </span>
                    {c.status && <ContestStatusBadge contest={c} />}
                    {winnerHref && (
                      <Link
                        to={winnerHref}
                        className="text-[10px] text-amber-300 hover:text-amber-100 underline font-black"
                        data-testid={`my-submission-view-result-${e.entry_id}`}
                      >
                        View win →
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyContestSubmissionsPage;
